/**
 * ProposalReview — the Direct Sold proposal as a checkbox table per month:
 * what ChannelMeter paid vs what the pipeline computes today (stored GAM
 * upload + current rate card + stored channel mapping), with the server's
 * flags, the month's provenance, and the control group (channels outside the
 * scope) that exposes rate-card or mapping drift. Selection is the reviewer's;
 * every figure is the server's.
 */

import React from 'react';
import { Pill, tableStyles } from '../../ui';
import type { DsProposal, ProposalLine, ProposalMonth } from '../../../services/earningsCorrectionsApi';
import {
  FLAG_META,
  formatCount,
  formatSignedCents,
  formatUsd,
  monthLabel,
  proposalLineKey,
  sourceFlagMeta,
  toCents,
} from './correctionModel';
import { cs, deltaColor, noticeStyle } from './correctionStyles';

interface ProposalReviewProps {
  proposal: DsProposal;
  selected: ReadonlySet<string>;
  onToggle: (key: string, on: boolean) => void;
  onToggleMonth: (month: ProposalMonth, on: boolean) => void;
}

const fmtWhen = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const ANCHOR_SOURCE: Record<ProposalMonth['source']['anchor']['source'], string> = {
  processing_run: 'the processing run ChannelMeter was given',
  cm_upload: 'the ChannelMeter upload confirmation',
  cm_lock: 'the ChannelMeter lock confirmation',
  none: 'no record',
};

const summaryStyle: React.CSSProperties = { cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--fg-2)', padding: '6px 0' };

const LineRow: React.FC<{ line: ProposalLine; lineKey: string; checked: boolean; onToggle: (key: string, on: boolean) => void }> = ({
  line,
  lineKey,
  checked,
  onToggle,
}) => {
  const delta = toCents(line.deltaEarningsUsd);
  const draftable = !!line.input;
  const otherGamNames = line.gamNames.filter((n) => n !== line.channelName);
  return (
    <tr style={{ opacity: draftable ? 1 : 0.65 }}>
      <td style={{ ...tableStyles.td, width: 28 }}>
        <input
          type="checkbox"
          aria-label={`Include ${line.channelName}`}
          checked={checked}
          disabled={!draftable}
          title={draftable ? undefined : 'No ChannelMeter payee this month to assign the row to. Use Manual mode.'}
          onChange={(e) => onToggle(lineKey, e.target.checked)}
        />
      </td>
      <td style={tableStyles.td}>
        <div style={{ fontWeight: 500 }}>{line.channelName}</div>
        {otherGamNames.length > 0 && <div style={cs.small}>GAM: {otherGamNames.join(', ')}</div>}
        <code style={cs.mono}>{line.channelId}</code>
      </td>
      <td style={tableStyles.td}>
        <div>{line.userName ?? <span style={cs.muted}>No payee</span>}</div>
        {line.ownerHints && line.ownerHints.length > 0 && (
          <div style={cs.small}>
            Suggested from {line.ownerHints[0].source === 'nearest_ds_month' ? 'Direct Sold' : 'AdSense'} in{' '}
            {monthLabel(line.ownerHints[0].month)}
            {line.ownerHints.length > 1 ? ` (+${line.ownerHints.length - 1} other)` : ''}
          </div>
        )}
        {line.op === 'remove' && <div style={cs.small}>Removes the Direct Sold row</div>}
      </td>
      <td style={tableStyles.tdNum}>
        {formatUsd(line.base.earningsUsd)} → <strong>{line.target ? formatUsd(line.target.earningsUsd) : 'removed'}</strong>
      </td>
      <td style={{ ...tableStyles.tdNum, fontWeight: 600, color: deltaColor(delta) }}>{formatSignedCents(delta)}</td>
      <td style={tableStyles.tdNum}>
        {formatUsd(line.base.grossUsd)} → {line.target ? formatUsd(line.target.grossUsd) : '—'}
      </td>
      <td style={tableStyles.tdNum}>
        {formatCount(line.base.performance)} → {line.target ? formatCount(line.target.performance) : '—'}
      </td>
      <td style={tableStyles.td}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {line.flags.map((f) => (
            <Pill key={f} tone={FLAG_META[f].tone} title={FLAG_META[f].description} style={{ padding: '2px 8px', fontSize: 11 }}>
              {FLAG_META[f].label}
            </Pill>
          ))}
        </div>
        {line.coveredBy && <div style={cs.small}>Owned by correction {line.coveredBy}</div>}
      </td>
    </tr>
  );
};

const MonthSection: React.FC<{
  month: ProposalMonth;
  selected: ReadonlySet<string>;
  onToggle: (key: string, on: boolean) => void;
  onToggleMonth: (month: ProposalMonth, on: boolean) => void;
}> = ({ month, selected, onToggle, onToggleMonth }) => {
  const keys = month.lines.map((_, i) => proposalLineKey(month.month, i));
  const draftableKeys = keys.filter((_, i) => month.lines[i].input);
  const selectedCount = draftableKeys.filter((k) => selected.has(k)).length;
  const selectedDelta = month.lines.reduce(
    (sum, l, i) => (l.input && selected.has(keys[i]) ? sum + toCents(l.deltaEarningsUsd) : sum),
    0,
  );
  const anchor = month.source.anchor;
  const rounding = month.unchanged.filter((u) => u.rounding).length;

  return (
    <div style={{ ...cs.section, padding: 16 }}>
      <div style={{ ...cs.header, marginBottom: 8 }}>
        <div>
          <div style={cs.row}>
            <h3 style={{ ...cs.title, fontSize: 15 }}>{monthLabel(month.month)}</h3>
            {month.source.flags.map((f) => {
              const meta = sourceFlagMeta(f);
              return (
                <Pill key={f} tone={meta.tone} title={meta.description} style={{ padding: '2px 8px', fontSize: 11 }}>
                  {meta.label}
                </Pill>
              );
            })}
          </div>
          <div style={cs.small}>
            GAM upload {month.source.uploadFileName ?? '—'} ({fmtWhen(month.source.uploadedAt)}) · ChannelMeter anchor:{' '}
            {anchor.at ? `${ANCHOR_SOURCE[anchor.source]}, ${fmtWhen(anchor.at)}` : ANCHOR_SOURCE.none}
            {anchor.pipelineDeltaUsd != null && ` · pipeline change since: ${formatSignedCents(toCents(anchor.pipelineDeltaUsd))}`}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12.5 }}>
          <div>
            ChannelMeter DS in scope: <strong>{formatUsd(month.totals.cmEarningsUsd)}</strong>
          </div>
          <div>
            Selected {selectedCount}/{draftableKeys.length}:{' '}
            <strong style={{ color: deltaColor(selectedDelta) }}>{formatSignedCents(selectedDelta)}</strong>
          </div>
        </div>
      </div>

      {month.warnings.length > 0 && (
        <div style={{ ...noticeStyle('warning'), marginBottom: 8 }}>
          <ul style={{ ...cs.list, paddingLeft: 16 }}>
            {month.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {month.lines.length === 0 ? (
        <div style={cs.small}>
          No differences with ChannelMeter for the {month.scopeChannelIds.length} channel(s) in scope
          {month.unchanged.length ? ` (${month.unchanged.length} unchanged${rounding ? `, ${rounding} within cent rounding` : ''})` : ''}.
        </div>
      ) : (
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={{ ...tableStyles.th, width: 28 }}>
                  <input
                    type="checkbox"
                    aria-label={`Select all ${monthLabel(month.month)} lines`}
                    checked={draftableKeys.length > 0 && selectedCount === draftableKeys.length}
                    disabled={draftableKeys.length === 0}
                    onChange={(e) => onToggleMonth(month, e.target.checked)}
                  />
                </th>
                <th style={tableStyles.th}>Channel</th>
                <th style={tableStyles.th}>Payee</th>
                <th style={tableStyles.thNum}>Earnings (CM → pipeline)</th>
                <th style={tableStyles.thNum}>Change</th>
                <th style={tableStyles.thNum}>Gross</th>
                <th style={tableStyles.thNum}>Impressions</th>
                <th style={tableStyles.th}>Flags</th>
              </tr>
            </thead>
            <tbody>
              {month.lines.map((line, i) => (
                <LineRow key={keys[i]} line={line} lineKey={keys[i]} checked={selected.has(keys[i])} onToggle={onToggle} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        {month.unchanged.length > 0 && month.lines.length > 0 && (
          <details>
            <summary style={summaryStyle}>
              Unchanged ({month.unchanged.length}
              {rounding ? `, ${rounding} within cent rounding` : ''})
            </summary>
            <ul style={cs.list}>
              {month.unchanged.map((u) => (
                <li key={`${u.channelId}|${u.userName}`}>
                  {u.channelName} · {u.userName} · {formatUsd(u.earningsUsd)}
                  {u.rounding ? ' (cent rounding)' : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
        <details>
          <summary style={summaryStyle}>
            Control group: channels outside the scope {formatSignedCents(toCents(month.control.deltaGrossUsd))} gross (
            {month.control.channels} channels)
          </summary>
          <div style={cs.help}>
            The same comparison for every other channel. Large moves here mean the rate card, the channel mapping or other GAM
            data changed too, not just this re-upload.
          </div>
          {month.control.movers.length > 0 && (
            <ul style={cs.list}>
              {month.control.movers.map((m) => (
                <li key={m.channelId}>
                  {m.channelName}: ChannelMeter {formatUsd(m.cmGrossUsd)} vs pipeline {formatUsd(m.summaryGrossUsd)} (
                  {formatSignedCents(toCents(m.deltaGrossUsd))})
                </li>
              ))}
            </ul>
          )}
        </details>
        {month.unmapped.length > 0 && (
          <details>
            <summary style={summaryStyle}>Unmapped GAM names ({month.unmapped.length}), never proposed</summary>
            <ul style={cs.list}>
              {month.unmapped.map((u) => (
                <li key={u.gamName}>
                  {u.gamName}: {formatUsd(u.grossUsd)} gross · {formatCount(u.impressions)} impressions
                </li>
              ))}
            </ul>
          </details>
        )}
        {month.undetailed.length > 0 && (
          <details>
            <summary style={summaryStyle}>ChannelMeter rows without source detail ({month.undetailed.length}), never corrected</summary>
            <ul style={cs.list}>
              {month.undetailed.map((u) => (
                <li key={`${u.channelId}|${u.userName}`}>
                  {u.channelName} · {u.userName} · {formatUsd(u.earningsUsd)}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
};

const ProposalReview: React.FC<ProposalReviewProps> = ({ proposal, selected, onToggle, onToggleMonth }) => (
  <div style={cs.stack}>
    {proposal.months.map((m) => (
      <MonthSection key={m.month} month={m} selected={selected} onToggle={onToggle} onToggleMonth={onToggleMonth} />
    ))}
  </div>
);

export default ProposalReview;
